import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { Container, Box, Typography, Button, Avatar, Chip } from '@mui/material';
import Chart from 'react-apexcharts';
import { fetTotalBalance } from '../../api/debank_api';
import { fetchHistoricalData } from '../../api/coinmarketcap_api';
import imgCoin from '../../assets/images/coin.png';
import { IoMdClose } from 'react-icons/io';
import { formatter } from '../../utils/utils';
import { InitialOptions } from '../../utils/constants';
import useDimensions from '../../dimension';

const Interval = {
  '1H': {
    interval: '5m',
    count: 12,
  },
  '1D': {
    interval: '1h',
    count: 24,
  },
  '1W': {
    interval: '12h',
    count: 14,
  },
  '1M': {
    interval: 'daily',
    count: 31,
  },
  '1Y': {
    interval: 'monthly',
    count: 12,
  },
  ALL: {
    interval: '60d',
    count: 12,
  },
};

const PriceChart = ({ investment, interval }) => {
  const [series, setSeries] = useState([]);
  const [dayChange, setDayChange] = useState(0);
  const [options, setOptions] = useState(InitialOptions);

  const { isLoading, data, isFetching, refetch } = useQuery('tokenPrice', () =>
    fetchHistoricalData(investment.id, Interval[interval].interval, Interval[interval].count),
  );

  useEffect(() => {
    refetch();
    // eslint-disable-next-line
  }, [investment, interval]);
  useEffect(() => {
    const prices = data?.data?.quotes;
    if (!prices?.length) return;
    const percent = prices[prices.length - 1].quote.USD.price / prices[0].quote.USD.price;
    if (percent >= 1.02) {
      setDayChange(1);
    } else if (percent <= 0.98) {
      setDayChange(-1);
    } else {
      setDayChange(0);
    }
    setSeries(prices?.map((item) => item.quote.USD.price));
    setOptions((prev) => {
      const newOptions = { ...prev };
      newOptions.xaxis.categories = data?.data[investment.coin]?.quotes.map((quote, index) => {
        return quote.timestamp;
      });

      return newOptions;
    });
    // eslint-disable-next-line
  }, [data]);

  useEffect(() => {
    let color = dayChange === 0 ? '#ECB533' : dayChange === 1 ? '#48BB78' : '#DE383D';
    const colorStops = [
      {
        offset: 0,
        color: '#4B24DC',
        opacity: 0.1,
      },
      {
        offset: 100,
        color: color,
        opacity: 1,
      },
    ];
    setOptions((prev) => {
      const newOptions = { ...prev };
      newOptions.fill.gradient.colorStops = colorStops;

      return newOptions;
    });
  }, [dayChange]);

  if (isLoading || isFetching) return null;
  return <Chart options={options} series={[{ name: 'Price', data: series }]} height="100%" width="100%" />;
};

const DeltaPercent = ({ investment, interval, onDeltaChange }) => {
  const [dayChange, setDayChange] = useState(0);

  const { data: checkData, refetch } = useQuery('PriceCheck', () =>
    fetchHistoricalData(investment.id, Interval[interval].interval, Interval[interval].count),
  );

  useEffect(() => {
    refetch();
    // eslint-disable-next-line
  }, [investment, interval]);
  useEffect(() => {
    const prices = checkData?.data?.quotes;
    if (!prices?.length) return;
    const percent = prices[prices.length - 1].quote.USD.price / prices[0].quote.USD.price;
    if (percent >= 1.02) {
      setDayChange(1);
    } else if (percent <= 0.98) {
      setDayChange(-1);
    } else {
      setDayChange(0);
    }
    let color = dayChange === 0 ? '#ECB533' : dayChange === 1 ? '#48BB78' : '#DE383D';
    let delta = (prices[prices.length - 1].quote.USD.price - prices[0].quote.USD.price) / prices[0].quote.USD.price;
    onDeltaChange(new Intl.NumberFormat('en-US', { signDisplay: 'exceptZero', style: 'percent' }).format(delta), color);
    // eslint-disable-next-line
  }, [checkData]);

  return null;
};

const active = {
  background:
    'linear-gradient(131.78deg, #EEEEEE 12.72%, #D4C78C 21.39%, #DEBF70 34.64%, #AE9555 44.61%, #A78C47 57.04%)',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontWeight: 'bold',
  minWidth: '15px',
};

const HeadCard = (props) => {
  const { protocol, setProtocol } = props;
  const { width } = useDimensions();
  const [interval, setIntervalValue] = useState('1H');
  const { isLoading, data, isFetching } = useQuery('totalBalance', () => fetTotalBalance());
  const [delta, setDelta] = useState(0);
  const [color, setColor] = useState('#FFFFFF');
  const handleDelete = () => {
    console.info('You clicked the delete icon.');
    setProtocol(null);
  };

  return (
    <Container
      className="main_content_wrap"
      maxWidth="lg"
      sx={{
        borderRadius: '10px',
        background: 'linear-gradient(92.91deg, rgba(19, 22, 30, 0.76) 1.78%, rgba(19, 22, 30, 0.62) 99.64%)',
        paddingY: 4,
        marginBottom: 4,
      }}
    >
      <Box
        display={'flex'}
        justifyContent="space-between"
        // alignItems={"center"}
        flexDirection={{ xs: 'column', sm: 'column', md: 'row' }}
      >
        {width > 700 ? (
          <Box display={'flex'}>
            {/* <img src={imgCoin} alt="coin" width={40} /> */}
            <Typography
              variant="h4"
              component="div"
              color={'white'}
              fontWeight={500}
              sx={{ fontFamily: 'Kanit', opacity: 0.5, marginLeft: 2 }}
            >
              Investments
            </Typography>
          </Box>
        ) : null}

        {width > 700 ? (
          <Box>
            {Object.keys(Interval).map((key, index) => (
              <Button
                key={key}
                size="small"
                variant="text"
                sx={{ ...(interval === key ? active : { color: '#979BAC', opacity: 0.5, minWidth: '15px' }) }}
                onClick={() => setIntervalValue(key)}
              >
                {key}
              </Button>
            ))}
          </Box>
        ) : null}
      </Box>
      <Box marginX={{ xs: 0, sm: 0, md: 2 }}>
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'column', md: 'row' }}>
          <Box>
            <Typography
              variant="subtitle1"
              gutterBottom
              component="div"
              sx={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontFamily: 'Kanit',
                fontWeight: 400,
                fontSize: '16px',
              }}
            >
              Portfolio balance
            </Typography>

            <Typography
              variant="h3"
              gutterBottom
              component="div"
              sx={{
                background: 'linear-gradient(112.98deg, #FFF4D1 8.47%, #F5D28F 23.3%, #675537 91.31%);',
                fontFamily: 'Kanit',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                textFillColor: 'transparent',
                fontStyle: 'normal',
                WebkitTextFillColor: 'transparent',
                fontWeight: 600,
                fontSize: width > 700 ? '72px' : '40px',
              }}
            >
              {isLoading || isFetching ? '-' : formatter.format(data.total_usd_value)}
            </Typography>
            <Box
              sx={{
                width: '100%',
                height: '0px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
              display={width > 700 ? 'none' : 'block'}
            ></Box>
            {protocol && (
              <Box
                marginTop={width > 700 ? 8 : 2}
                display="flex"
                justifyContent={'space-between'}
                alignItems={'center'}
              >
                <Box display={'flex'} alignItems={'center'} flexDirection={{ xs: 'column', sm: 'row' }}>
                  {width > 700 ? (
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      component="div"
                      sx={{
                        color: 'white',
                        fontFamily: 'Kanit',
                        fontWeight: 500,
                      }}
                    >
                      Showing:
                    </Typography>
                  ) : null}

                  <Chip
                    sx={{
                      height: width > 700 ? 'auto' : '24px',
                      borderRadius: 20,
                      color: 'white',
                      marginLeft: width > 700 ? 2 : 0,
                      paddingY: width > 700 ? '0px' : '15px',
                    }}
                    deleteIcon={<IoMdClose color="white" />}
                    onDelete={handleDelete}
                    avatar={
                      <Avatar alt="Natacha" sx={{ width: '16px', height: '16px' }} src="/static/images/avatar/1.jpg" />
                    }
                    label={
                      <Box>
                        {width > 700 ? (
                          <Typography
                            variant="subtitle1"
                            component="div"
                            sx={{
                              color: 'white',
                              fontFamily: 'Kanit',
                              fontWeight: 500,
                            }}
                          >
                            {protocol.protocol}
                          </Typography>
                        ) : null}

                        <Typography
                          variant="subtitle1"
                          component="div"
                          sx={{
                            color: 'white',
                            fontFamily: 'Kanit',
                            fontWeight: 300,
                            marginTop: width > 700 ? -1 : 0,
                          }}
                        >
                          {protocol.coin}
                        </Typography>
                      </Box>
                    }
                    variant="outlined"
                  />
                </Box>
                <Box display={width > 700 ? 'none' : 'flex'}>
                  {Object.keys(Interval).map((key, index) => (
                    <Button
                      key={key}
                      size="small"
                      variant="text"
                      sx={{ ...(interval === key ? active : { color: '#979BAC', opacity: 0.5, minWidth: '15px' }) }}
                      onClick={() => setIntervalValue(key)}
                    >
                      {key}
                    </Button>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
          {protocol && !isLoading && !isFetching && (
            <Box sx={{ flexGrow: 1 }}>
              <Container maxWidth="sm" sx={{ marginTop: 2 }}>
                <Typography
                  variant="subtitle1"
                  component="div"
                  sx={{
                    color: color,
                    fontFamily: 'Kanit',
                    fontWeight: 400,
                    textAlign: 'end',
                  }}
                >
                  {delta ? delta : '?'}
                </Typography>
              </Container>
              <PriceChart investment={protocol} interval={interval} />
            </Box>
          )}
        </Box>
      </Box>
      <DeltaPercent
        investment={protocol}
        interval={interval}
        onDeltaChange={(delta, color) => {
          setDelta(delta);
          setColor(color);
        }}
      />
    </Container>
  );
};

export default HeadCard;
